import React, { useEffect, useState } from 'react';
//Constants
import { PROFILE_API, TECHNOLOGY_API } from '../constants/configs';
import { API_REQUEST, CANDIDATE_LOCATIONS, LABELS } from '../constants/general';
//Utils
import API from '../utils/api';
import { getFormErrorMessage, isFormFieldValid, trimStringsInObject } from '../utils/general';
import { dispatch } from '../utils/store';
import { handleError, handleSuccess } from '../utils/toast';
//Components
import Label from './Label';
//Utility Packages
import { useFormik } from 'formik';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { MultiSelect } from 'primereact/multiselect';
import { RadioButton } from 'primereact/radiobutton';
import { classNames } from 'primereact/utils';
import DatePicker from 'react-datepicker';
import CreatableSelect from 'react-select/creatable';
//Redux
import { useSelector } from 'react-redux';
import { Actions } from '../redux/actions';
import { ResumeUserDetailValidationSchema } from '../constants/schemas';
const userData = {
  firstName: '',
  middleName: '',
  lastName: '',
  phone: '',
  secondPhone: '',
  gender: '',
  currentCTC: null,
  currentCompany: '',
  currentAddress: '',
  permanentAddress: '',
  currentLocation: '',
  location: '',
  dateOfBirth: '',
  maritalStatus: '',
  totalExperience: null,
  language: '',
  technologies: [],
  softSkill: ''
};
const UserDetailForm = ({ isEditable, setIsEditable }) => {
  //Store
  const toastReff = useSelector((state) => state.notifications.toastReff);
  const { resumeData, userDetails } = useSelector((state) => state.resumeFetchDetails);
  //State
  const [technologies, setTechnologies] = useState([]);
  const [languageInput, setLanguageInput] = useState('');
  const [softSkillInput, setSoftSkillInput] = useState('');
  const [languageSuggestion, setLanguageSuggestion] = useState([]);
  const [softSkillSuggestion, setSoftSkillSuggestion] = useState([]);
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: userDetails ? userDetails : userData,
    validationSchema: ResumeUserDetailValidationSchema,
    onSubmit: (values) => {
      updateProfileData(
        trimStringsInObject({
          ...values,
          technologies: values.technologies.toString(),
          currentLocation:
            values.currentLocation === 'other'
              ? values.location?.trim()
              : values.currentLocation?.trim()
        })
      );
    }
  });

  const updateProfileData = (data) => {
    dispatch(Actions.User.SetLoading, true);

    const payload = new FormData();
    Object.keys(data).forEach((key) => {
      const value = data[key];
      if (
        value === null ||
        value === undefined ||
        value === '' ||
        (Array.isArray(value) && value.length === 0)
      ) {
        return;
      }

      if (Array.isArray(value)) {
        payload.append(key, value.map((t) => t.value).toString());
      } else {
        payload.append(key, value);
      }
    });

    API(API_REQUEST.put, PROFILE_API, payload)
      .then((res) => {
        handleSuccess(toastReff, res);
        setIsEditable(true);
        dispatch(Actions.ResumeFetchDetails.SetUserDetails, formik.values);
        dispatch(Actions.User.SetLoading, false);
      })
      .catch((error) => {
        dispatch(Actions.User.SetLoading, false);
        handleError(toastReff, error);
        formik.resetForm({ values: userDetails });
        console.log(error);
      });
  };
  useEffect(() => {
    if (!userDetails) {
      const personalDetails = resumeData?.BasicDetails || {};
      const formatAddress = (address) => {
        return [
          address?.street,
          address?.area,
          address?.city,
          address?.state,
          address?.postalCode,
          address?.country
        ]
          .filter(Boolean)
          .join(', ');
      };
      const years = personalDetails?.totalExperience?.years ?? 0;
      const months = personalDetails?.totalExperience?.months ?? 0;
      const formattedEx = `${Math.floor(Number(years))}.${Math.floor(Number(months)).toString().padStart(2, '0')}`;
      let isLocationFromDropdown =
        CANDIDATE_LOCATIONS.find((location) =>
          location.code.toLowerCase().includes(personalDetails?.currentAddress?.city?.toLowerCase())
        )?.code || 'other';
      const modifiedData = {
        ...userDetails,
        firstName: personalDetails?.firstName || '',
        middleName: personalDetails?.middleName || '',
        lastName: personalDetails?.lastName || '',
        phone:
          personalDetails?.phones[0]?.trim()?.length > 10
            ? personalDetails?.phones[0]?.trim().slice(-10)
            : personalDetails?.phones[0]?.trim() || null,
        secondPhone:
          personalDetails?.phones[1]?.trim()?.length > 10
            ? personalDetails?.phones[1]?.trim().slice(-10)
            : personalDetails?.phones[1]?.trim() || null,
        gender: personalDetails?.gender || null,
        currentCTC: personalDetails?.Experience?.find((exp) => exp.isCurrentEmployment)?.lastSalary
          ? parseFloat(
              personalDetails?.Experience?.find((exp) => exp.isCurrentEmployment)?.lastSalary
            ).toFixed(1)
          : null,
        dateOfBirth: personalDetails?.dateOfBirth || null,
        maritalStatus: personalDetails?.maritalStatus || null,
        currentCompany:
          resumeData?.Experience?.find((exp) => exp.isCurrentEmployment)?.company || null,
        currentAddress: formatAddress(personalDetails?.currentAddress),
        permanentAddress: formatAddress(personalDetails?.permanentAddress),
        currentLocation: personalDetails?.currentAddress?.city
          ? isLocationFromDropdown
            ? isLocationFromDropdown
            : 'other'
          : null,
        location: personalDetails?.currentAddress?.city
          ? isLocationFromDropdown
            ? personalDetails?.currentAddress?.city
            : null
          : null,
        language: personalDetails?.languages
          ? personalDetails?.languages.map((item) => ({
              label: item.trim(),
              value: item.trim()
            }))
          : '',
        technologies:
          technologies
            .filter((tech) =>
              resumeData?.Technologies?.some(
                (resumeTech) =>
                  resumeTech.trim().toLowerCase() === tech?.Technology?.trim()?.toLowerCase()
              )
            )
            .map((tech) => tech.ID) || [],
        softSkill: resumeData?.Skills
          ? resumeData?.Skills.map((item) => ({
              label: item.trim(),
              value: item.trim()
            }))
          : '',
        totalExperience: formattedEx
      };
      dispatch(Actions.ResumeFetchDetails.SetUserDetails, modifiedData);
      formik.setValues(modifiedData);
    }
  }, [resumeData, technologies]);

  useEffect(() => {
    getTechnology();
  }, []);

  const getTechnology = () => {
    dispatch(Actions.User.SetLoading, true);
    API(API_REQUEST.get, TECHNOLOGY_API)
      .then((res) => {
        dispatch(Actions.User.SetLoading, false);
        setTechnologies(res?.data?.data?.data || []);
      })
      .catch((error) => {
        dispatch(Actions.User.SetLoading, false);
        console.log(error);
      });
  };
  const handleLanguageSuggestion = (e) => {
    const options = languageSuggestion.concat(e);
    const newOptions = options.filter(
      (item, index) => options.findIndex((obj) => obj.value === item.value) === index
    );
    setLanguageSuggestion(newOptions);
  };

  const handleSoftSkillSuggestion = (e) => {
    const options = softSkillSuggestion.concat(e);
    const newOptions = options.filter(
      (item, index) => options.findIndex((obj) => obj.value === item.value) === index
    );
    setSoftSkillSuggestion(newOptions);
  };
  return (
    <div className="mt-5">
      <form onSubmit={formik.handleSubmit} className="form-main">
        <div className="profile-info-wrapper user-profile-wrapper">
          <div className="form-row-wrapper">
            <div className="form-col form-col-3">
              <div className="form-group-outer">
                <div className="custom-form-group">
                  <Label htmlFor="firstName" text={LABELS.INPUT.FIRSTNAME} isBold />
                  <InputText
                    id="firstName"
                    name="firstName"
                    placeholder={LABELS.INPUT.FIRSTNAME}
                    value={formik.values.firstName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    disabled={isEditable}
                    className={classNames({
                      'p-invalid': isFormFieldValid(formik, 'firstName')
                    })}
                  />
                </div>
                {getFormErrorMessage(formik, 'firstName')}
              </div>
            </div>
            <div className="form-col form-col-3">
              <div className="form-group-outer">
                <div className="custom-form-group">
                  <Label htmlFor="middleName" text={LABELS.INPUT.MIDDLENAME} isBold />
                  <InputText
                    id="middleName"
                    name="middleName"
                    placeholder={LABELS.INPUT.MIDDLENAME}
                    value={formik.values.middleName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    disabled={isEditable}
                    className={classNames({
                      'p-invalid': isFormFieldValid(formik, 'middleName')
                    })}
                  />
                </div>
                {getFormErrorMessage(formik, 'middleName')}
              </div>
            </div>
            <div className="form-col form-col-3">
              <div className="form-group-outer">
                <div className="custom-form-group">
                  <Label htmlFor="lastName" text={LABELS.INPUT.LASTNAME} isBold />
                  <InputText
                    id="lastName"
                    name="lastName"
                    placeholder={LABELS.INPUT.LASTNAME}
                    value={formik.values.lastName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    disabled={isEditable}
                    className={classNames({
                      'p-invalid': isFormFieldValid(formik, 'lastName')
                    })}
                  />
                </div>
                {getFormErrorMessage(formik, 'lastName')}
              </div>
            </div>
            <div className="form-col form-col-3">
              <div className="form-group-outer">
                <div className="custom-form-group">
                  <Label htmlFor="phone" text={LABELS.INPUT.PHONE} isBold />
                  <InputNumber
                    id="phone"
                    name="phone"
                    placeholder={LABELS.INPUT.PHONE}
                    value={formik.values.phone}
                    className={classNames({
                      'p-invalid': isFormFieldValid(formik, 'phone')
                    })}
                    onValueChange={(e) => formik.setFieldValue('phone', e.value)}
                    mode="decimal"
                    useGrouping={false}
                    maxLength={10}
                    disabled={isEditable}
                  />
                </div>
                {getFormErrorMessage(formik, 'phone')}
              </div>
            </div>
            <div className="form-col form-col-3">
              <div className="form-group-outer">
                <div className="custom-form-group">
                  <Label htmlFor="secondPhone" text={LABELS.INPUT.SECOND_PHONE} isBold />
                  <InputNumber
                    id="secondPhone"
                    name="secondPhone"
                    placeholder={LABELS.INPUT.PHONE}
                    value={formik.values.secondPhone}
                    className={classNames({
                      'p-invalid': isFormFieldValid(formik, 'secondPhone')
                    })}
                    onValueChange={(e) => {
                      const value = e.value === null || e.value === undefined ? '' : e.value;
                      formik.setFieldValue('secondPhone', value);
                    }}
                    mode="decimal"
                    useGrouping={false}
                    maxLength={10}
                    disabled={isEditable}
                  />
                </div>
                {getFormErrorMessage(formik, 'secondPhone')}
              </div>
            </div>
            <div className="form-col form-col-3">
              <div className="form-group-outer">
                <div className="custom-form-group">
                  <Label htmlFor="currentLocation" text={LABELS.INPUT.CURRENT_LOCATION} />
                  <Dropdown
                    id="currentLocation"
                    name="currentLocation"
                    value={formik.values.currentLocation}
                    optionLabel="name"
                    optionValue="code"
                    options={CANDIDATE_LOCATIONS}
                    onChange={formik.handleChange}
                    panelClassName="custom-dropdown-panel"
                    disabled={isEditable}
                    className={classNames({
                      'p-invalid': isFormFieldValid(formik, 'currentLocation'),
                      'w-full': true
                    })}
                  />
                </div>
                {getFormErrorMessage(formik, 'currentLocation') ||
                  getFormErrorMessage(formik, 'location')}
              </div>
            </div>
            {formik.values.currentLocation === 'other' && (
              <div className="form-col form-col-3">
                <div className="form-group-outer">
                  <div className="custom-form-group">
                    <Label />
                    <InputText
                      id="location"
                      name="location"
                      value={formik.values.location}
                      placeholder={LABELS.INPUT.CURRENT_LOCATION}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      disabled={isEditable}
                      className={classNames('candidate-location-other', {
                        'p-invalid': isFormFieldValid(formik, 'location'),
                        'w-full': true
                      })}
                    />
                  </div>
                </div>
              </div>
            )}
            <div className="form-col form-col-3">
              <div className="form-group-outer">
                <div className="custom-form-group date-picker-wrapper">
                  <Label htmlFor="dateOfBirth" text={LABELS.INPUT.DOB} isBold />
                  <DatePicker
                    id="dateOfBirth"
                    name="dateOfBirth"
                    dateFormat="dd/MM/yyyy"
                    placeholderText="Date"
                    peekNextMonth
                    showMonthDropdown
                    showYearDropdown
                    dropdownMode="select"
                    selected={formik.values.dateOfBirth}
                    onChange={(date) => formik.setFieldValue('dateOfBirth', date)}
                    maxDate={new Date().setFullYear(new Date().getFullYear() - 18)}
                    disabled={isEditable}
                    className={classNames({
                      'p-invalid': isFormFieldValid(formik, 'dateOfBirth')
                    })}
                  />
                </div>
                {getFormErrorMessage(formik, 'dateOfBirth')}
              </div>
            </div>
            <div className="form-col form-col-3">
              <div className="form-group-outer">
                <div className="custom-form-group">
                  <Label htmlFor="totalExperience" text={LABELS.INPUT.TOTAL_EXP} isBold />
                  <InputNumber
                    id="totalExperience"
                    name="totalExperience"
                    placeholder={LABELS.INPUT.TOTAL_EXP_PLACEHOLDER}
                    value={formik.values.totalExperience}
                    className={classNames({
                      'p-invalid': isFormFieldValid(formik, 'totalExperience')
                    })}
                    onValueChange={(e) => formik.setFieldValue('totalExperience', e.value)}
                    mode="decimal"
                    minFractionDigits={2}
                    maxFractionDigits={2}
                    min={0}
                    max={100}
                    disabled={isEditable}
                  />
                </div>
                {getFormErrorMessage(formik, 'totalExperience')}
              </div>
            </div>
            <div className="form-col form-col-3">
              <div className="form-group-outer">
                <div className="custom-form-group">
                  <Label htmlFor="currentCompany" text={LABELS.INPUT.CURRENT_COMPANY} isBold />
                  <InputText
                    id="currentCompany"
                    name="currentCompany"
                    value={formik.values.currentCompany}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    disabled={isEditable}
                    className={classNames({
                      'p-invalid': isFormFieldValid(formik, 'currentCompany')
                    })}
                  />
                </div>
                {getFormErrorMessage(formik, 'currentCompany')}
              </div>
            </div>
            <div className="form-col form-col-3">
              <div className="form-group-outer">
                <div className="custom-form-group">
                  <Label htmlFor="currentCTC" text={LABELS.INPUT.CURRENT_CTC} isBold />
                  <div className="p-inputgroup flex-1 salary-wrapper">
                    <InputNumber
                      id="currentCTC"
                      name="currentCTC"
                      value={formik.values.currentCTC}
                      onValueChange={(e) => formik.setFieldValue('currentCTC', e.value)}
                      className={classNames({
                        'p-invalid': isFormFieldValid(formik, 'currentCTC')
                      })}
                      mode="decimal"
                      min={0}
                      max={99}
                      minFractionDigits={1}
                      maxFractionDigits={1}
                      disabled={isEditable}
                    />
                    <span className="p-inputgroup-addon">LPA</span>
                  </div>
                </div>
                {getFormErrorMessage(formik, 'currentCTC')}
              </div>
            </div>
            <div className="form-col form-col-3">
              <div className="form-group-outer">
                <div className="custom-form-group margin-bottom">
                  <Label htmlFor="gender" text={LABELS.INPUT.GENDER} isBold />
                  <div className="radio-list-wrapper">
                    <div className="form-group-outer-radiobutton">
                      <RadioButton
                        inputId="male"
                        value={1}
                        name="gender"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        checked={formik.values.gender === 1}
                        disabled={isEditable}
                      />
                      <Label htmlFor="male" text={LABELS.INPUT.MALE} isBold />
                    </div>
                    <div className="form-group-outer-radiobutton">
                      <RadioButton
                        inputId="female"
                        value={2}
                        name="gender"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        checked={formik.values.gender === 2}
                        disabled={isEditable}
                      />
                      <Label htmlFor="female" text={LABELS.INPUT.FEMALE} isBold />
                    </div>
                  </div>
                </div>
                {getFormErrorMessage(formik, 'gender')}
              </div>
            </div>
            <div className="form-col form-col-3">
              <div className="form-group-outer">
                <div className="custom-form-group margin-bottom">
                  <Label htmlFor="maritalStatus" text={LABELS.INPUT.MARITAL_STATUS} isBold />
                  <div className="radio-list-wrapper">
                    <div className="form-group-outer-radiobutton">
                      <RadioButton
                        inputId="single"
                        value={1}
                        name="maritalStatus"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        checked={formik.values.maritalStatus === 1}
                        disabled={isEditable}
                      />
                      <Label htmlFor="single" text={LABELS.INPUT.SINGLE} isBold />
                    </div>
                    <div className="form-group-outer-radiobutton">
                      <RadioButton
                        inputId="married"
                        value={2}
                        name="maritalStatus"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        checked={formik.values.maritalStatus === 2}
                        disabled={isEditable}
                      />
                      <Label htmlFor="married" text={LABELS.INPUT.MARRIED} isBold />
                    </div>
                  </div>
                </div>
                {getFormErrorMessage(formik, 'maritalStatus')}
              </div>
            </div>
            <div className="form-col has-full-width">
              <div className="form-group-outer">
                <div className="custom-form-group autocomplete">
                  <Label htmlFor="language" text={LABELS.INPUT.LANGUAGE} isBold />
                  <CreatableSelect
                    isMulti
                    hideSelectedOptions={false}
                    id="language"
                    name="language"
                    value={formik.values.language}
                    onBlur={formik.handleBlur}
                    onChange={(e) => {
                      const normalizedOptions = e.map((item) => ({
                        ...item,
                        value: item.value.toLowerCase(),
                        label: item.label
                      }));
                      formik.setFieldValue('language', normalizedOptions);
                      handleLanguageSuggestion(e);
                    }}
                    options={languageSuggestion.map((item) => ({
                      ...item,
                      value: item.value.toLowerCase()
                    }))}
                    placeholder={LABELS.INPUT.LANGUAGE}
                    className={classNames({
                      'p-invalid': isFormFieldValid(formik, 'language')
                    })}
                    classNamePrefix="custom-create-select"
                    inputValue={languageInput}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        e.stopPropagation();
                        if (e.target.value) {
                          const newOption = {
                            label: e.target.value,
                            value: e.target.value.toLowerCase()
                          };
                          const existingOptions = formik.values.language || [];
                          const isDuplicate = existingOptions.some(
                            (option) => option.value === newOption.value
                          );
                          if (!isDuplicate) {
                            formik.setFieldValue('language', [...existingOptions, newOption]);
                            handleLanguageSuggestion([newOption]);
                          }
                          setLanguageInput('');
                        }
                      }
                    }}
                    onInputChange={(inputValue) => {
                      setLanguageInput(inputValue);
                    }}
                    isDisabled={isEditable}
                  />
                </div>
                {getFormErrorMessage(formik, 'language')}
              </div>
            </div>
            <div className="form-col has-full-width">
              <div className="form-group-outer">
                <div className="custom-form-group">
                  <Label htmlFor="technologies" text={LABELS.INPUT.TECHNOLOGY} isBold />
                  <MultiSelect
                    filter
                    name="technologies"
                    id="technologies"
                    optionLabel="Technology"
                    optionValue="ID"
                    value={formik.values.technologies}
                    forceSelection
                    options={technologies}
                    onChange={formik.handleChange}
                    display="chip"
                    onBlur={formik.handleBlur}
                    placeholder="Select Technology *"
                    disabled={isEditable}
                    className={classNames({
                      'p-invalid': isFormFieldValid(formik, 'technologies'),
                      'w-full': true
                    })}
                    panelClassName="technology-overlay profile-technology"
                  />
                </div>
                {getFormErrorMessage(formik, 'technologies')}
              </div>
            </div>

            <div className="form-col has-full-width">
              <div className="form-group-outer">
                <div className="custom-form-group autocomplete">
                  <Label htmlFor="softSkill" text={LABELS.INPUT.SOFT_SKILLS} isBold />
                  <CreatableSelect
                    isMulti
                    hideSelectedOptions={false}
                    id="softSkill"
                    name="softSkill"
                    value={formik.values.softSkill}
                    onBlur={formik.handleBlur}
                    onChange={(e) => {
                      const normalizedOptions = e.map((item) => ({
                        ...item,
                        value: item.value.toLowerCase(),
                        label: item.label
                      }));
                      formik.setFieldValue('softSkill', normalizedOptions);
                      handleSoftSkillSuggestion(e);
                    }}
                    options={softSkillSuggestion.map((item) => ({
                      ...item,
                      value: item.value.toLowerCase()
                    }))}
                    placeholder={LABELS.INPUT.SOFT_SKILLS}
                    className={classNames({
                      'p-invalid': isFormFieldValid(formik, 'softSkill')
                    })}
                    classNamePrefix="custom-create-select"
                    inputValue={softSkillInput}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        e.stopPropagation();
                        if (e.target.value) {
                          const newOption = {
                            label: e.target.value,
                            value: e.target.value.toLowerCase()
                          };
                          const existingOptions = formik.values.softSkill || [];
                          const isDuplicate = existingOptions.some(
                            (option) => option.value === newOption.value
                          );
                          if (!isDuplicate) {
                            formik.setFieldValue('softSkill', [...existingOptions, newOption]);
                            handleSoftSkillSuggestion([newOption]);
                          }
                          setSoftSkillInput('');
                        }
                      }
                    }}
                    onInputChange={(inputValue) => {
                      setSoftSkillInput(inputValue);
                    }}
                    isDisabled={isEditable}
                  />
                </div>
                {getFormErrorMessage(formik, 'softSkill')}
              </div>
            </div>
            <div className="form-col ">
              <div className="form-group-outer">
                <div className="custom-form-group">
                  <Label htmlFor="currentAddress" text={LABELS.INPUT.CURRENTADDRESS} isBold />
                  <InputTextarea
                    placeholder={LABELS.INPUT.CURRENTADDRESS}
                    labelText={LABELS.INPUT.CURRENTADDRESS}
                    value={formik.values.currentAddress}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    errorMessage={
                      formik.touched['currentAddress'] ? formik.errors['currentAddress'] : ''
                    }
                    disabled={isEditable}
                    className={classNames({
                      'p-invalid': isFormFieldValid(formik, 'currentAddress')
                    })}
                    rows={5}
                    cols={30}
                    id="currentAddress"
                    name="currentAddress"
                  />
                </div>
                {getFormErrorMessage(formik, 'currentAddress')}
              </div>
            </div>
            <div className="form-col">
              <div className="form-group-outer">
                <div className="custom-form-group">
                  <Label htmlFor="permanentAddress" text={LABELS.INPUT.PERMANENTADDRESS} isBold />
                  <InputTextarea
                    placeholder={LABELS.INPUT.PERMANENTADDRESS}
                    labelText={LABELS.INPUT.PERMANENTADDRESS}
                    value={formik.values.permanentAddress}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    errorMessage={
                      formik.touched['permanentAddress'] ? formik.errors['permanentAddress'] : ''
                    }
                    disabled={isEditable}
                    className={classNames({
                      'p-invalid': isFormFieldValid(formik, 'permanentAddress')
                    })}
                    rows={5}
                    cols={30}
                    id="permanentAddress"
                    name="permanentAddress"
                  />
                </div>
                {getFormErrorMessage(formik, 'permanentAddress')}
              </div>
            </div>
          </div>
          <div className="submit-btn-block">
            {!isEditable && (
              <Button type="submit" variant="contained">
                {LABELS.BUTTON.SAVE}
              </Button>
            )}
          </div>
        </div>
      </form>
    </div>
  );
};

export default UserDetailForm;
